import React, { useState } from "react";
import MobileHeader from "./MobileHeader";
import MobileListRow from "./MobileListRow";

// The four top-level quadrants of the desktop layout become the root of the
// mobile navigation tree — one screen per level. Tapping a quadrant pushes a
// full-screen view; the elements quadrant then drills further into its own
// tree of tables (handled inside ElementTable's mobile mode).
type Quadrant = "network" | "charts" | "elements" | "renewables";

interface QuadrantMeta {
  id: Quadrant;
  icon: string;
  label: string;
  description: string;
}

const QUADRANTS: QuadrantMeta[] = [
  {
    id: "network",
    icon: "🕸️",
    label: "Network",
    description: "Grid diagram — buses, lines, generation",
  },
  {
    id: "charts",
    icon: "📈",
    label: "Load & Prices",
    description: "Demand curve, battery storage, LMP surface",
  },
  {
    id: "elements",
    icon: "📋",
    label: "Elements",
    description: "Tables, objective, variables & constraints",
  },
  {
    id: "renewables",
    icon: "🌤️",
    label: "Renewables",
    description: "Wind and solar availability curves",
  },
];

interface Props {
  // The toolbar (scenario / edit / add menus) is shown on the home screen and
  // above the network canvas so editing stays reachable.
  toolbar: React.ReactNode;
  network: React.ReactNode;
  charts: React.ReactNode;
  renewables: React.ReactNode;
  // Elements owns its own drill-down + header; it calls onExit to return home.
  renderElements: (onExit: () => void) => React.ReactNode;
}

const MobileApp: React.FC<Props> = ({
  toolbar,
  network,
  charts,
  renewables,
  renderElements,
}) => {
  const [route, setRoute] = useState<Quadrant | null>(null);
  const goHome = () => setRoute(null);

  const shell = (children: React.ReactNode): React.ReactElement => (
    <div
      style={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        background: "white",
        overflow: "hidden",
      }}
    >
      {children}
    </div>
  );

  // Home: the list of quadrants.
  if (route === null) {
    return shell(
      <>
        {toolbar}
        <div style={{ flex: 1, minHeight: 0, overflowY: "auto", background: "#f5f5f5" }}>
          {QUADRANTS.map((q) => (
            <MobileListRow
              key={q.id}
              icon={q.icon}
              label={q.label}
              description={q.description}
              drillsDown
              onClick={() => setRoute(q.id)}
            />
          ))}
        </div>
      </>
    );
  }

  const meta = QUADRANTS.find((q) => q.id === route)!;

  // Elements manages its own header + breadcrumb + drill-down, so render it
  // without the shared header wrapper.
  if (route === "elements") {
    return shell(renderElements(goHome));
  }

  const content =
    route === "network" ? network : route === "charts" ? charts : renewables;

  return shell(
    <>
      <MobileHeader title={meta.label} onBack={goHome} />
      {route === "network" && toolbar}
      <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {content}
      </div>
    </>
  );
};

export default MobileApp;
